import {RoomState} from '@lib/infrastructure';
import {add, addMinutes, isAfter, isBefore, parseISO} from 'date-fns';
import {MeetingRoomSuggestion, ScheduleInformationItem} from '../../data';
import {Timeslot, Interval} from './types';

export const mapScheduleInformationToTimeslot = (scheduleInformation: ScheduleInformationItem): Timeslot => ({
  start: parseISO(scheduleInformation.start),
  end: parseISO(scheduleInformation.end),
});

const getTimeslotsWithinInterval = (
  timeslots?: Timeslot[],
  {minStartDateString, maxStartDateString}: Interval = {},
) => {
  if (!minStartDateString && !maxStartDateString) return timeslots;

  return timeslots?.filter(
    ({start}) =>
      (!minStartDateString || !isBefore(start, parseISO(minStartDateString))) &&
      (!maxStartDateString || !isAfter(start, parseISO(maxStartDateString))),
  );
};

export const getTimeSlotsThatStartsWithinInterval = (
  scheduleInformation?: ScheduleInformationItem[],
  interval?: Interval,
) => {
  const timeslots = scheduleInformation?.map(mapScheduleInformationToTimeslot);
  return getTimeslotsWithinInterval(timeslots, interval);
};

export const getLastAboutToEndTimeslot = (
  startDate: string,
  aboutToEndDurationInMinutes: number,
  scheduleInformation?: ScheduleInformationItem[],
) => {
  const timeslots = scheduleInformation?.reduce((acc, current) => {
    const timeslot = mapScheduleInformationToTimeslot(current);
    if (isAfter(timeslot.end, addMinutes(parseISO(startDate), aboutToEndDurationInMinutes))) return acc;

    return [...acc, timeslot];
  }, [] as Timeslot[]);

  return timeslots && timeslots[timeslots.length - 1];
};
export const calculateDynamicFreeTimeslot = (
  roomAvailabilityPeriodOfInterest: number,
  suggestionData: MeetingRoomSuggestion | undefined,
  startDateString: string,
  durationOfTimeslot: Duration,
): Timeslot | undefined => {
  if (!suggestionData) return;
  const {scheduleInformation, state: roomState} = suggestionData;

  if (RoomState.anyKindOfBooked(roomState)) return;

  const lastAboutToEndTimeslot = getLastAboutToEndTimeslot(
    startDateString,
    roomAvailabilityPeriodOfInterest,
    scheduleInformation,
  );

  // If there is a last about to end timeslot, then the actual start date is the end of that timeslot.
  const actualStartDate = RoomState.anyKindOfBookingAboutToEnd(roomState)
    ? lastAboutToEndTimeslot!.end
    : parseISO(startDateString);

  const desiredEndDate = add(actualStartDate, durationOfTimeslot);
  const nextBookedTimeSlot = getTimeSlotsThatStartsWithinInterval(scheduleInformation, {
    minStartDateString: actualStartDate.toISOString(),
    maxStartDateString: desiredEndDate.toISOString(),
  })?.[0];

  // If there is a next booked timeslot, then the actual end date is the start of that timeslot.
  const actualEndDateTime = nextBookedTimeSlot?.start ?? desiredEndDate;

  return {
    start: actualStartDate,
    end: actualEndDateTime,
  };
};
